<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACCOUNT_BALANCE_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              {{ $t('PDF') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="customerAccountBalanceTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(ledgerBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(availableBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(overdrawnAmount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(accruedOverdraftInterest)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="customerAccountBalanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
          <!-- Account Name -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
          <!-- Product -->
          </b-row>

          <b-row class="match-height">
            <!-- Financial Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date"
                v-model="filter.financialDate"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date')"
                :error-name="$t('Financial Date')"
                :config="datePickerConfig"
                :placeholder="$t('Financial Date')"
              />
            </b-col>
          <!-- Financial Date -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import { heightFade } from '@core/directives/animations'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import { toCustomDate, toDateWithYearFirst, getMinusOneDayDate } from '@/helpers/formatDate'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'
import DatePickerField from '../../../forms/DatePickerField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
    // BFormDatepicker,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toFormatAmount,
      toBalance,
      formatToDateWithYears: toDateWithYearFirst,
      currentDate: '',
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      isDisabled: true,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account Balance Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'bankVerificationNumber', sortable: true, label: 'BVN' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'customerPhone', sortable: true, label: 'Customer Phone' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'ledgerBalance', sortable: true, label: 'Ledger Balance' },
        { key: 'availableBalance', sortable: true, label: 'Available Balance' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDate: null,
        productCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      productOptions: [],
      reportData: [],
      allSearchedData: [],
      selected: '',
      branchRefCode: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    getPreviousFinancialDates() {
      return getMinusOneDayDate(this.currentFinancialDate.financialDate)
    },
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        BVN: item.bankVerificationNumber,
        Branch: item.branch,
        Product: item.product,
        'Customer Phone': item.customerPhone,
        Gender: item.gender,
        'Ledger Balance': formatAmount(item.ledgerBalance),
        'Available Balance': formatAmount(item.availableBalance),
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomerAccountBalance()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomerAccountBalance()
    },
  },
  mounted() {
    this.datePickerConfig.maxDate = this.getPreviousFinancialDates
    // this.fetchCustomerAccountBalance()
    this.fetchProduct()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getAccountBalances: 'reports/getAccountBalances',
      getBranchByCode: 'operations/getBranchByCode',
      downloadCustomerAccountBalanceReports: 'reports/downloadCustomerAccountBalanceReports',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.fetchCustomerAccountBalance()
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.customerAccountBalanceRules.validate().then(async success => {
        if (success) {
          this.fetchCustomerAccountBalance()
        }
      })
    },
    async fetchCustomerAccountBalance() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        this.isDisabled = false
        const response = await this.getAccountBalances({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCustomerAccountBalanceReports({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.ACCOUNT_BALANCE_REPORT,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Customer Account Balance Report.csv')
          this.alertSuccess({ text: 'Your request to download Customer Account Balance Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCustomerAccountBalanceReports({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_REPORT,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          FileSaver.saveAs(blob, 'Customer Account Balance Report.xlsx')
          this.alertSuccess({ text: 'Your request to download Customer Account Balance Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadCustomerAccountBalanceReports({
          search: this.filter,
          fileExportFormat: 2,
          permission: reportPermissions.ACCOUNT_BALANCE_REPORT,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Customer Account Balance Report.pdf')
          this.alertSuccess({ text: 'Your request to download Customer Account Balance Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
